import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileArchive, FolderOpen, Upload } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router';
import { importApi } from '../../api/import';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { Spinner } from '../../components/ui/spinner';
import { cn } from '../../lib/cn';
import { paths } from '../../lib/routes';
import { useUi } from '../../stores/ui';
import { entriesFromFiles } from './archive';
import { planKeepImport, type ImportPlan } from './keep-plan';
import { runImport, type ImportProgress, type ImportReport } from './run-import';

type Step =
  | { name: 'choose'; error?: string }
  | { name: 'reading'; done: number; total: number }
  | { name: 'ready'; plan: ImportPlan }
  | { name: 'importing'; progress: ImportProgress }
  | { name: 'done'; report: ImportReport; plan: ImportPlan };

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString('fr-FR')} ${n > 1 ? many : one}`;

function ProgressBar({ value, total, label }: { value: number; total: number; label: string }) {
  const percent = total ? Math.round((value / total) * 100) : 100;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-xs text-muted">
        <span>{label}</span>
        <span>
          {value.toLocaleString('fr-FR')} / {total.toLocaleString('fr-FR')}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-1.5 overflow-hidden rounded-full bg-hover"
      >
        <div
          className="h-full rounded-full bg-accent transition-[width]"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function Warnings({ items }: { items: Array<{ source: string; message: string }> }) {
  if (items.length === 0) return null;
  return (
    <details className="rounded border border-line text-sm">
      <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-muted">
        <AlertTriangle size={16} className="shrink-0" />{' '}
        {plural(items.length, 'avertissement', 'avertissements')}
      </summary>
      <ul className="scrollbar-thin max-h-48 overflow-y-auto border-t border-line px-3 py-2 text-xs">
        {items.map((w, i) => (
          <li key={i} className="py-0.5">
            <span className="font-medium">{w.source}</span> : {w.message}
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Import d'un export Google Takeout (Keep) : tout est analysé dans le navigateur avant envoi. */
export function ImportDialog() {
  const open = useUi((s) => s.importOpen);
  const setOpen = useUi((s) => s.setImportOpen);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ name: 'choose' });
  const [includeTrashed, setIncludeTrashed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const zipInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep({ name: 'choose' });
    setIncludeTrashed(false);
  };

  const close = () => {
    if (step.name === 'importing' || step.name === 'reading') return;
    setOpen(false);
    reset();
  };

  const analyse = async (files: File[]) => {
    if (files.length === 0) return;
    setStep({ name: 'reading', done: 0, total: 0 });
    try {
      const entries = await entriesFromFiles(files);
      const plan = await planKeepImport(entries, (done, total) =>
        setStep({ name: 'reading', done, total }),
      );
      if (plan.notes.length === 0) {
        setStep({
          name: 'choose',
          error:
            'Aucune note Google Keep trouvée. Vérifiez que l’export Takeout contient bien « Keep » (fichiers .json des notes).',
        });
        return;
      }
      setStep({ name: 'ready', plan });
    } catch (error) {
      setStep({
        name: 'choose',
        error: error instanceof Error ? error.message : 'Lecture impossible',
      });
    }
  };

  const start = async (plan: ImportPlan) => {
    abort.current = new AbortController();
    const report = await runImport(
      plan,
      importApi,
      { includeTrashed, signal: abort.current.signal },
      (progress) => setStep({ name: 'importing', progress }),
    );
    abort.current = null;
    await queryClient.invalidateQueries();
    setStep({ name: 'done', report, plan });
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    void analyse(Array.from(event.dataTransfer.files));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => (value ? setOpen(true) : close())}
      title="Importer depuis Google Keep"
      className="max-w-[560px]"
    >
      <div className="scrollbar-thin flex flex-col gap-4 overflow-y-auto px-6 pb-2 text-sm">
        {step.name === 'choose' && (
          <>
            <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-fg">
              <li>
                Ouvrez{' '}
                <a
                  href="https://takeout.google.com/settings/takeout/custom/keep"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Google Takeout
                </a>{' '}
                et laissez seulement <strong>Keep</strong> coché.
              </li>
              <li>
                Créez l’export puis téléchargez l’archive <code>.zip</code> reçue par e-mail.
              </li>
              <li>Sélectionnez-la ici (ou le dossier « Keep » une fois l’archive extraite).</li>
            </ol>
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={cn(
                'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors',
                dragging ? 'border-accent bg-hover' : 'border-line',
              )}
            >
              <Upload size={32} className="text-muted" aria-hidden />
              <p className="text-muted">Déposez l’archive ici, ou</p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="primary" onClick={() => zipInput.current?.click()}>
                  <FileArchive size={18} /> Choisir l’archive
                </Button>
                <Button className="border border-line" onClick={() => folderInput.current?.click()}>
                  <FolderOpen size={18} /> Choisir un dossier
                </Button>
              </div>
              <input
                ref={zipInput}
                type="file"
                accept=".zip,.json,application/zip"
                multiple
                hidden
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  event.target.value = '';
                  void analyse(files);
                }}
              />
              <input
                ref={(el) => {
                  folderInput.current = el;
                  // Attribut non standard (mais universel) pour choisir un dossier entier.
                  el?.setAttribute('webkitdirectory', '');
                }}
                type="file"
                hidden
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  event.target.value = '';
                  void analyse(files);
                }}
              />
            </div>
            <p className="text-xs text-muted">
              L’archive est lue dans votre navigateur : seules les notes et leurs images sont
              envoyées. Un export découpé en plusieurs .zip peut être sélectionné en une fois.
            </p>
            {step.error && (
              <p role="alert" className="text-danger">
                {step.error}
              </p>
            )}
          </>
        )}

        {step.name === 'reading' && (
          <div className="flex flex-col items-center gap-3 py-8 text-muted">
            <Spinner />
            <p>
              Analyse de l’archive…
              {step.total > 0 &&
                ` ${step.done.toLocaleString('fr-FR')} / ${step.total.toLocaleString('fr-FR')}`}
            </p>
          </div>
        )}

        {step.name === 'ready' && (
          <>
            <p>Voici ce qui a été trouvé :</p>
            <ul className="flex flex-col gap-1 rounded-lg bg-hover px-4 py-3">
              <li>
                <strong>{plural(step.plan.stats.notes, 'note', 'notes')}</strong>
                {step.plan.stats.checklists > 0 &&
                  ` dont ${plural(step.plan.stats.checklists, 'liste', 'listes')}`}
              </li>
              {step.plan.stats.pinned > 0 && (
                <li>{plural(step.plan.stats.pinned, 'épinglée', 'épinglées')}</li>
              )}
              {step.plan.stats.archived > 0 && (
                <li>{plural(step.plan.stats.archived, 'archivée', 'archivées')}</li>
              )}
              {step.plan.labels.length > 0 && (
                <li>{plural(step.plan.labels.length, 'libellé', 'libellés')}</li>
              )}
              {step.plan.stats.images > 0 && (
                <li>{plural(step.plan.stats.images, 'image', 'images')}</li>
              )}
            </ul>
            {step.plan.stats.trashed > 0 && (
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeTrashed}
                  onChange={(event) => setIncludeTrashed(event.target.checked)}
                  className="size-4 accent-[var(--primary)]"
                />
                Importer aussi les {plural(step.plan.stats.trashed, 'note', 'notes')} de la
                corbeille (supprimées sous 7 jours)
              </label>
            )}
            <Warnings items={step.plan.warnings} />
            <p className="text-xs text-muted">
              Les dates, couleurs, épingles, archives et libellés sont conservés. Les rappels ne
              figurent pas dans l’export Google. Vous pouvez relancer l’import à tout moment : les
              notes déjà importées sont ignorées.
            </p>
          </>
        )}

        {step.name === 'importing' && (
          <div className="flex flex-col gap-4 py-4">
            <ProgressBar
              value={step.progress.notesDone}
              total={step.progress.notesTotal}
              label="Notes"
            />
            {step.progress.imagesTotal > 0 && (
              <ProgressBar
                value={step.progress.imagesDone}
                total={step.progress.imagesTotal}
                label="Images"
              />
            )}
            <p className="text-xs text-muted">Gardez cet onglet ouvert pendant l’import.</p>
          </div>
        )}

        {step.name === 'done' && (
          <>
            <div className="flex items-start gap-3">
              {step.report.interrupted ? (
                <AlertTriangle className="shrink-0 text-danger" aria-hidden />
              ) : (
                <CheckCircle2 className="shrink-0 text-[#188038] dark:text-[#81c995]" aria-hidden />
              )}
              <div className="flex flex-col gap-1">
                <p className="font-medium">
                  {step.report.interrupted
                    ? `Import interrompu : ${step.report.interrupted}`
                    : 'Import terminé'}
                </p>
                <p>
                  {plural(step.report.created, 'note importée', 'notes importées')}
                  {step.report.skipped > 0 &&
                    `, ${plural(step.report.skipped, 'déjà présente', 'déjà présentes')}`}
                  {step.report.images > 0 && `, ${plural(step.report.images, 'image', 'images')}`}
                  {step.report.labels > 0 &&
                    `, ${plural(step.report.labels, 'libellé créé', 'libellés créés')}`}
                  .
                </p>
                {step.report.interrupted && (
                  <p className="text-muted">
                    Relancez l’import avec la même archive : il reprendra où il s’est arrêté.
                  </p>
                )}
              </div>
            </div>
            <Warnings items={step.report.failures} />
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 px-4 py-3">
        {step.name === 'choose' && <Button onClick={close}>Fermer</Button>}
        {step.name === 'ready' && (
          <>
            <Button onClick={reset}>Retour</Button>
            <Button variant="primary" onClick={() => void start(step.plan)}>
              Importer{' '}
              {plural(
                step.plan.stats.notes - (includeTrashed ? 0 : step.plan.stats.trashed),
                'note',
                'notes',
              )}
            </Button>
          </>
        )}
        {step.name === 'importing' && (
          <Button onClick={() => abort.current?.abort()}>Annuler</Button>
        )}
        {step.name === 'done' && (
          <>
            {step.report.interrupted && (
              <Button onClick={() => setStep({ name: 'ready', plan: step.plan })}>Reprendre</Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                close();
                navigate(paths.notes);
              }}
            >
              Voir mes notes
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
}
